import React, { forwardRef } from 'react';
import styled, { keyframes } from 'styled-components';
import SortIcon from '../SortIcon';
import { SimpleFlexContainer as FlexContainer } from '../../../components/layouts/ng-layouts/Containers';
import Animation from '../../../components/Animation';
import LoopIcon from '@material-ui/icons/Loop';
import useTranslations from '../../../hooks/useTranslations';

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(-360deg);
  }
`;

const LoadingIndicator = styled(LoopIcon)`
  animation: ${rotate} 2s linear infinite;
`;

const Container = styled(FlexContainer)`
  ${({ $customCSS }) => $customCSS}
  max-height: ${({ $maxHeight }) =>
    $maxHeight === 'auto' ? $maxHeight : `${$maxHeight}px`};
  min-height: ${({ $maxHeight }) =>
    $maxHeight === 'auto' ? $maxHeight : `${$maxHeight}px`};
`;

const Table = styled.div`
  display: grid;
  grid-template-columns: ${({ $colsLayout }) => $colsLayout};
  overflow: auto;
`;

const Cell = styled(FlexContainer)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  padding: 0 1rem;
  height: 55px;
  background: #fff;

  position: relative;

  ${({ $rightDivider, theme, $customDividerCSS }) =>
    $rightDivider &&
    `  &:after {
        transform: translate(0, 50%);
        content: "";
        background: ${theme.palette.divider};
        position: absolute;
        bottom: 50%;
        left: 0;
        height: 50%;
        width: 1px;
        ${$customDividerCSS}
      }`}

  ${({ $leftDivider, theme, $customDividerCSS }) =>
    $leftDivider &&
    `  &:before {
        transform: translate(0, 50%);
        content: "";
        background: ${theme.palette.divider};
        position: absolute;
        bottom: 50%;
        left: 0;
        height: 50%;
        width: 1px;
        ${$customDividerCSS}
      }`}
  ${({ $customCellCSS }) => $customCellCSS}
  ${({ $customCSS }) => $customCSS}
`;

const LoadingCell = styled(Cell)`
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
`;

const HeaderCol = styled(Cell)`
  ${({ $sortable }) => $sortable && 'cursor: pointer;'}
  align-items: center;
  font-weight: 600;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 2;

  ${({ $customCSS }) => $customCSS}
`;

const ColText = styled(FlexContainer)`
  flex: 1;
`;

const NoRecords = styled(Cell)`
  align-items: center;
  justify-content: center;
  font-weight: 600;
`;

const DataGridView = forwardRef((props, ref) => {
  const t = useTranslations();
  return (
    <Container
      ref={ref}
      {...props.otherProps}
      $customCSS={props.customCSS}
      $maxHeight={props.listMaxHeight}
    >
      <Table
        data-id="table"
        ref={props.tableRef}
        $colsLayout={props.colsSettings.map(cs => cs.width).join(' ')}
      >
        {props.columns.map(
          ({ id, display, customCSS, disableSort, ...col }) => (
            <HeaderCol
              id={id}
              data-id="table-header"
              key={id}
              $sortable={props.sortable && !disableSort}
              $row
              onClick={() => props.onSortChange(id)}
              $customCSS={customCSS}
            >
              <ColText>
                {typeof display === 'string' ? t(display) : display}
              </ColText>
              {props.sortable && !disableSort && (
                <SortIcon
                  sort={props.sort.id === id ? props.sort.value : undefined}
                />
              )}
            </HeaderCol>
          )
        )}

        {props.loading ? (
          <LoadingCell
            $row
            style={{ gridColumn: `1 / span ${props.columns.length}` }}
          >
            <LoadingIndicator /> {t('system.grid.loading')}
          </LoadingCell>
        ) : props.rows.length ? (
          props.rows.map(row =>
            props.columns.map(({ id, ...col }) => (
              <Cell
                key={id}
                data-id="table-cell"
                $customCSS={row.customCSS}
                $customCellCSS={row.customCellCSS}
                $leftDivider={col.leftDivider}
                $rightDivider={col.rightDivider}
                $customDividerCSS={props.customDividerCSS}
              >
                <Animation $row>
                  {typeof row[id] === 'string' ? t(row[id]) : row[id]}
                </Animation>
              </Cell>
            ))
          )
        ) : (
          <NoRecords style={{ gridColumn: `1 / span ${props.columns.length}` }}>
            {props.noRecordsText || t('data-grid.no-records')}
          </NoRecords>
        )}
      </Table>
    </Container>
  );
});

export default DataGridView;
